import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, CircleNotch } from "@phosphor-icons/react";
import api from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/posts")
      .then((r) => setPosts(r.data.posts || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page">
      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
        <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold flex items-center gap-2">
          <BookOpen size={14} weight="duotone" /> Mine AI Journal
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 text-[#0A1128] tracking-tight max-w-3xl leading-[1.05]">
          Honest jewellery advice,
          <br className="hidden sm:block" />
          <span className="gold-text">straight from our experts</span>.
        </h1>
        <p className="mt-4 text-[#4A5568] max-w-2xl">
          Weekly notes on hallmarks, fair pricing, regional craftsmanship, and what to watch for before you buy.
        </p>

        {loading ? (
          <div className="mt-12 flex items-center gap-3 text-[#4A5568]">
            <CircleNotch className="animate-spin" weight="bold" /> Loading articles...
          </div>
        ) : posts.length === 0 ? (
          <div className="mt-12 mc-card p-10 text-center" data-testid="blog-empty">
            <p className="text-[#4A5568]">
              No articles yet. Our editors are sharpening their first one.
            </p>
            <Link
              to="/chat"
              className="mt-4 inline-flex items-center gap-2 text-sm text-[#3B5BDB] font-medium hover:gap-3 smooth"
            >
              Have a question? Ask Mine AI <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        ) : (
          <div className="mt-12 grid sm:grid-cols-2 gap-6 lg:gap-8">
            {posts.map((p, i) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="mc-card lift smooth overflow-hidden flex flex-col fade-up"
                style={{ animationDelay: `${i * 70}ms` }}
              >
                {p.cover_image_url ? (
                  <div className="aspect-[16/9] bg-[#F5EFE6] overflow-hidden">
                    <img
                      src={p.cover_image_url}
                      alt=""
                      className="w-full h-full object-cover smooth hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/9] bg-gradient-to-br from-[#3B5BDB]/10 via-[#D4AF37]/10 to-[#F5EFE6] flex items-center justify-center">
                    <BookOpen size={36} weight="duotone" className="text-[#D4AF37] opacity-60" />
                  </div>
                )}
                <div className="p-6 flex-1 flex flex-col">
                  {p.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {p.tags.slice(0, 3).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#3B5BDB]/5 text-[#3B5BDB]"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                  <h2 className="font-display text-2xl text-[#0A1128] leading-tight">
                    {p.title}
                  </h2>
                  {p.excerpt && (
                    <p className="text-sm text-[#4A5568] mt-3 leading-relaxed line-clamp-3">
                      {p.excerpt}
                    </p>
                  )}
                  <div className="mt-5 pt-4 border-t border-[#E8DFD1] flex items-center justify-between">
                    <span className="text-xs text-[#4A5568]">
                      {p.published_at
                        ? new Date(p.published_at).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : ""}
                    </span>
                    <span className="text-sm text-[#3B5BDB] font-medium flex items-center gap-1">
                      Read <ArrowRight size={14} weight="bold" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
